import { reportReadFailure } from "@istari-frontend/shared";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { useConversationMessages } from "~/ai-chat/data-source/use-conversations";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import type { ChatMessage } from "~/ai-chat/model/chat-message";
import type { ConversationMessage } from "~/ai-chat/model/conversation";

function toTranscriptItem(message: ConversationMessage): ChatMessage {
  return {
    kind: "message",
    id: message.id,
    role: message.role,
    content: message.content,
    createdAt: message.created,
    ...(message.toolCalls.length > 0 ? { toolCalls: message.toolCalls } : {}),
  };
}

// Loads a selected conversation's persisted messages into the transcript when switching
// TO it (not during its own live turn — the unsynced flag protects the in-flight reply).
// Mount this once at a level that outlives the history list (the page / widget shell) so
// switching still hydrates with the list closed — e.g. from the chat tabs.
//
// A switch paints the cached snapshot at once — unless the provider put the conversation's
// own live transcript back (it was streaming when the user left), which is fresher than any
// snapshot. Either way, the first fetch that lands after the switch is applied once the
// conversation's run has settled: the snapshot can predate the turn the server is still
// writing (its assistant row lands in the stream's `finally`), and the live transcript's
// reply is replaced by the persisted row. That pass replaces message items with the server
// rows but keeps the local-only items (error bubbles, approval cards), which have no server row.
export function useHydrateActiveConversation() {
  const { messages, activeConversationId, replaceMessages, isConversationUnsynced, runningConversationIds } =
    useAiChat();
  const { data, error, isError, isFetching, dataUpdatedAt } = useConversationMessages(activeConversationId);
  const switchedIdRef = useRef<string | null>(null);
  // Armed by a switch until a fetch newer than the painted snapshot has been merged in.
  const pendingFreshRef = useRef<{ id: string; snapshotUpdatedAt: number } | null>(null);
  const isRunning = activeConversationId !== null && runningConversationIds.has(activeConversationId);

  useEffect(() => {
    if (activeConversationId === null) {
      switchedIdRef.current = null;
      pendingFreshRef.current = null;
      return;
    }
    // The id also changes when the first send persists a live chat — its turn hasn't
    // finished appending server-side yet, so hydrating would wipe the in-flight reply.
    // A send on the conversation on screen passes through here too, so the arm stays
    // set: the pass runs once that turn settles and fills in what the snapshot missed.
    if (isConversationUnsynced(activeConversationId)) {
      switchedIdRef.current = activeConversationId;
      return;
    }
    if (activeConversationId !== switchedIdRef.current) {
      switchedIdRef.current = activeConversationId;
      pendingFreshRef.current = { id: activeConversationId, snapshotUpdatedAt: dataUpdatedAt };
      if (messages.length === 0 && data !== undefined) replaceMessages(data.map(toTranscriptItem));
      return;
    }
    const pending = pendingFreshRef.current;
    if (pending === null || pending.id !== activeConversationId || isRunning || isFetching) return;
    // The observer suppresses the query cache's own report, and the snapshot on screen may be
    // missing the reply the user came back for — say so instead of leaving it looking current.
    if (isError) {
      pendingFreshRef.current = null;
      reportReadFailure("ai-chat.conversation-messages", error, { conversationId: activeConversationId });
      toast.error("Couldn't refresh the conversation. Reopen it to try again.");
      return;
    }
    if (data === undefined || dataUpdatedAt === pending.snapshotUpdatedAt) return;
    pendingFreshRef.current = null;
    replaceMessages([...data.map(toTranscriptItem), ...messages.filter((item) => item.kind !== "message")]);
  }, [
    activeConversationId,
    data,
    error,
    isError,
    isFetching,
    dataUpdatedAt,
    isRunning,
    messages,
    replaceMessages,
    isConversationUnsynced,
  ]);
}
